import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../store/globalState";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePreventReload } from "lola-framework-ui-test/src/hooks";
import {
  AuraLayout,
  Button,
  Layout,
} from "lola-framework-ui-test";
import { APP_ROUTES, flavorClassName, TITLE_COLOR } from "../constants";
import { FloatingField } from "../components/FloatingField";
import { defaultCardOriginCountryCode, resolveCardOriginCountryCodes } from "../constants/countries";
import { useSessionStore } from "../store/useSessionStore";
import type { CardStepFields } from "./AddCardPage";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useVisualViewportPin } from "../hooks/useVisualViewportPin";
import { useSurfaceAccent } from "../hooks/useSurfaceAccent";
import { getEvents, safeCapture } from "../analytics";
import { countryDisplayName } from "../utils/country-display";
import { statesForCountry } from "../utils/states";
import {
  loadZipLookup,
  zipLookupKey,
  type ZipLookup,
} from "../utils/zip-lookup";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";
import { CloseLight } from "../assets/navIcons";


export interface AddressFormFields {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressFormErrors {
  street1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface AddCardFullState {
  card?: CardStepFields;
  address: AddressFormFields;
}

export const AddCardAddressPage = memo(() => {
  useClaimLink();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const cardOriginPolicy =
    session.data?.config?.cardPolicy?.cardOriginPolicy ?? "us_only";
  const cardOriginCountryCodes = useMemo(
    () =>
      resolveCardOriginCountryCodes(
        session.data?.config?.corridors,
        cardOriginPolicy
      ),
    [cardOriginPolicy, session.data?.config?.corridors]
  );
  const lockedCountryCode =
    cardOriginCountryCodes.length === 1 ? cardOriginCountryCodes[0] : "";
  const isCountryLocked = Boolean(lockedCountryCode);
  const initialCountryCode = defaultCardOriginCountryCode(
    cardOriginCountryCodes
  );

  const cardData = (location.state as { card?: CardStepFields })?.card;

  useEffect(() => {
    safeCapture(getEvents().BILLING_VIEW);
  }, []);

  useEffect(() => {
    if (!cardData) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
    }
  }, [cardData, redirectTo]);

  const [form, setForm] = useState<AddressFormFields>({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
    country: initialCountryCode,
  });
  const [errors, setErrors] = useState<AddressFormErrors>({});

  const selectedCountryCode = isCountryLocked
    ? lockedCountryCode
    : form.country;
  const isUS = selectedCountryCode === "US";
  const stateOptions = useMemo(
    () => statesForCountry(selectedCountryCode),
    [selectedCountryCode]
  );

  /**
   * US billing only asks for street and ZIP: the dataset resolves city and
   * state, so the payload keeps its shape without the two extra fields. Other
   * countries have no equivalent dataset and keep collecting them by hand.
   */
  const [zipLookup, setZipLookup] = useState<ZipLookup | null>(null);

  useEffect(() => {
    if (!isUS) return;
    let active = true;
    loadZipLookup()
      .then((lookup) => {
        if (active) setZipLookup(() => lookup);
      })
      .catch(() => {
        // `installChunkReload` reloads on preload failures. Until then the CTA
        // stays disabled rather than submitting an address with no city.
      });
    return () => {
      active = false;
    };
  }, [isUS]);

  const zipLocation = useMemo(() => {
    if (!isUS || !zipLookup) return null;
    const key = zipLookupKey(form.zipCode);
    return key ? zipLookup(key) : null;
  }, [form.zipCode, isUS, zipLookup]);

  const resolvedCity = isUS ? (zipLocation?.city ?? "") : form.city;
  const resolvedState = isUS ? (zipLocation?.stateCode ?? "") : form.state;
  const canSubmit = isUS
    ? Boolean(form.street1.trim()) && Boolean(zipLocation)
    : true;
  const zipUnknown =
    isUS &&
    Boolean(zipLookup) &&
    Boolean(zipLookupKey(form.zipCode)) &&
    !zipLocation;

  useVisualViewportPin();
  usePreventReload();

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;
  const accentSolid = useSurfaceAccent();
  const onDarkSurface = flavorClassName !== "flavor-a";
  const controlColor = onDarkSurface ? "var(--foreground)" : bodyCopy;
  const fieldColors = {
    background: onDarkSurface ? "var(--background)" : "#fff",
    title: onDarkSurface ? "var(--foreground)" : colorsConfig.title,
    bodyCopy: controlColor,
    inactiveColor: onDarkSurface
      ? "var(--muted-foreground)"
      : colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };

  const handleChange = (field: keyof AddressFormFields, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country" && value !== prev.country) {
        next.state = "";
      }
      return next;
    });
    if (errors[field as keyof AddressFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): AddressFormErrors => {
    const errs: AddressFormErrors = {};
    if (!form.street1.trim())
      errs.street1 = t("billing.streetRequired", {
        defaultValue: "Street address is required",
      });
    if (!isUS) {
      if (!form.city.trim())
        errs.city = t("billing.cityRequired", {
          defaultValue: "City is required",
        });
      if (!form.state.trim())
        errs.state = t("billing.stateRequired", {
          defaultValue: "State is required",
        });
    }

    if (isUS) {
      if (!/^\d{5}(-\d{4})?$/.test(form.zipCode))
        errs.zipCode = t("billing.zipRequired", {
          defaultValue: "Valid zip code is required",
        });
      else if (!zipLocation)
        errs.zipCode = t("billing.zipNotFound", {
          defaultValue: "We couldn't find that ZIP code",
        });
    } else {
      if (!form.zipCode.trim() || form.zipCode.trim().length < 2)
        errs.zipCode = t("billing.postalCodeRequired", {
          defaultValue: "Valid postal code is required",
        });
    }

    if (
      !selectedCountryCode ||
      !cardOriginCountryCodes.includes(selectedCountryCode)
    ) {
      errs.country = t("billing.countryRequired", {
        defaultValue: "Country is required",
      });
    }

    return errs;
  };

  const handleContinue = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    redirectTo(APP_ROUTES.ADD_CARD_PROCESSING_PAGE, {
      state: {
        card: cardData,
        address: {
          ...form,
          city: resolvedCity,
          state: resolvedState,
          country: selectedCountryCode,
        },
      } satisfies AddCardFullState,
    });
  };
  const saveArmed = useRef(false);
  const pressSave = (event?: React.MouseEvent<HTMLButtonElement>) => {
    const fromKeyboard = event?.detail === 0;
    if (!fromKeyboard && !saveArmed.current) return;
    saveArmed.current = false;
    handleContinue();
  };

  if (!cardData) return null;

  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;
  if (!canSend) {
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    return null;
  }

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <AuraLayout colorConfig={colorsConfig} className={`white-view-background ${flavorClassName}`}>
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={t("billing.title", { defaultValue: "Billing Address" })}
          onBackClick={() => navigate(-1)}
          icon={<CloseLight size={36} colors={[accentSolid, accentSolid]} />}
          onIconClick={() => redirectTo(APP_ROUTES.TRANSFER_PAGE)}
        />
      </Layout.Header>

      <Layout.Content isOverflowauto>
        <ScrollFadeIndicator color={bodyCopy} />
        <div className="cardpage-content">
          <div>
            <FloatingField
              label={t("billing.street1", { defaultValue: "Street Address" })}
              value={form.street1}
              required
              isValid={!errors.street1}
              colors={fieldColors}
            >
              <input
                type="text"
                style={{ color: controlColor }}
                value={form.street1}
                onChange={(e) => handleChange("street1", e.target.value)}
                autoFocus
              />
            </FloatingField>
            {errors.street1 && <div style={errorStyle}>{errors.street1}</div>}
          </div>

          {!isUS && (
            <>
              <div>
                <FloatingField
                  label={t("billing.selectState", { defaultValue: "State" })}
                  value={form.state}
                  required
                  isValid={!errors.state}
                  colors={fieldColors}
                >
                  {stateOptions.length > 0 ? (
                    <select
                      style={{ color: controlColor, cursor: "pointer" }}
                      value={form.state}
                      onChange={(e) => handleChange("state", e.target.value)}
                    >
                      <option value="" />
                      {stateOptions.map((state) => (
                        <option key={state.fullCode} value={state.code}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      autoComplete="address-level1"
                      style={{ color: controlColor }}
                      value={form.state}
                      onChange={(e) => handleChange("state", e.target.value)}
                    />
                  )}
                </FloatingField>
                {errors.state && <div style={errorStyle}>{errors.state}</div>}
              </div>

              <div>
                <FloatingField
                  label={t("billing.city", { defaultValue: "City" })}
                  value={form.city}
                  required
                  isValid={!errors.city}
                  colors={fieldColors}
                >
                  <input
                    type="text"
                    style={{ color: controlColor }}
                    value={form.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                  />
                </FloatingField>
                {errors.city && <div style={errorStyle}>{errors.city}</div>}
              </div>
            </>
          )}

          <div>
            <FloatingField
              label={t("billing.zipCode", { defaultValue: "Zip Code" })}
              value={form.zipCode}
              required
              isValid={!errors.zipCode}
              colors={fieldColors}
            >
              <input
                type="text"
                inputMode={isUS ? "numeric" : "text"}
                style={{ color: controlColor }}
                value={form.zipCode}
                onChange={(e) =>
                  handleChange(
                    "zipCode",
                    isUS
                      ? e.target.value.replace(/[^\d-]/g, "")
                      : e.target.value
                  )
                }
                maxLength={10}
              />
            </FloatingField>
            {(errors.zipCode || zipUnknown) && (
              <div style={errorStyle}>
                {errors.zipCode ??
                  t("billing.zipNotFound", {
                    defaultValue: "We couldn't find that ZIP code",
                  })}
              </div>
            )}
          </div>

          {!isCountryLocked && (
            <div>
              <FloatingField
                label={t("billing.country", { defaultValue: "Country" })}
                value={form.country}
                required
                isValid={!errors.country}
                colors={fieldColors}
              >
                <select
                  style={{ color: controlColor, cursor: "pointer" }}
                  value={form.country}
                  onChange={(e) => handleChange("country", e.target.value)}
                >
                  {cardOriginCountryCodes.map((countryCode) => (
                    <option key={countryCode} value={countryCode}>
                      {countryDisplayName(
                        countryCode,
                        i18n.language || "en"
                      )}
                    </option>
                  ))}
                </select>
              </FloatingField>
              {errors.country && <div style={errorStyle}>{errors.country}</div>}
            </div>
          )}
        </div>
      </Layout.Content>
      <Layout.Footer>
        <Button
          size="small"
          disabled={!canSubmit}
          color={textColorBtn}
          background={backgroundBtn}
          onPointerDown={() => {
            saveArmed.current = true;
          }}
          onClick={pressSave}
        >
          {t("card.continue")}
        </Button>
      </Layout.Footer>
    </AuraLayout>
  );
});
